// Gestión de zonas sociales. Equivale a estas pantallas de la app:
//   gestionar_zonas_sociales.dart       → listado, accesos a Autorizaciones y Estadísticas
//   seleccionar_zonas.dart              → habilitar una zona (con sus reglas por defecto)
//   eliminar_zonas_sociales_alerta.dart → quitar una zona (solo apaga el interruptor)
//   editar_mis_zonas_sociales.dart      → reglas de la zona
// Las reglas se guardan en coordi_ph_clientes/{codigo}/{zonaId} con los mismos campos que
// EditarZonaModel (coordiph/lib/models/modelo_editar_zona.dart).
import { useState, useEffect, memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AnimacionCarga from "./animacion_de_carga";
import BarraSuperior from "./barra_superior";
import BotonSOS from "./sos/boton_sos";
import { ZONAS_CATALOGO, zonasActivasDelConjunto } from "../../functions/zonas_catalogo";
import { cargarConjunto, cargarZona, crearZona, ZONA_POR_DEFECTO } from "../../functions/reservas";
import { HORAS_INTERMEDIAS, fechaHoraDart } from "../../functions/reservas_reglas";
import { actualizarRegistro } from "../../functions/crud";
import { subirArchivoFirebase } from "../../functions/archivos_firebase";
import "bootstrap-icons/font/bootstrap-icons.css";

const R = "#c0392b";
const TEXTO_DEPOSITO_POR_DEFECTO = "Esta zona tiene un costo por deposito:";

// Campos de EditarZonaModel: al guardar solo se conservan estos (igual que toJson() en la app).
const CAMPOS_ZONA = [
  "HoraInicio", "requiereAforo", "reservaUnica", "cantidadSedes", "solicitarDatosVisitantes", "HoraFinal",
  "ObservacionesYRecomendaciones", "AforoHora", "DepositoBool", "DepositoValor", "DepositoTexto",
  "RequiereAutorizacion", "LimiteHoras", "LimitePersonas", "agregarDetalles", "adjuntarPdf", "urlPdf",
  "bloquearHora", "reservaPorDia", "reservaPorSemana", "reservaPorMes", "reservaLunes", "reservaMartes",
  "reservaMiercoles", "reservaJueves", "reservaViernes", "reservaSabado", "reservaDomingo", "horaCompleta",
  "proximaReserva", "usuario", "fecha", "contarPendientes", "proximaReservaDescanso",
];
const soloCamposZona = (z) => Object.fromEntries(CAMPOS_ZONA.filter(k => z[k] !== undefined && z[k] !== null).map(k => [k, z[k]]));

// Valores que la app pone cuando se abre el editor de una zona sin configuración.
const ZONA_NUEVA_EDITOR = {
  reservaUnica: true, contarPendientes: true, DepositoBool: false, agregarDetalles: false, adjuntarPdf: false,
  DepositoValor: 0, HoraFinal: "22:00", HoraInicio: "06:00", LimiteHoras: 1, LimitePersonas: 1,
  ObservacionesYRecomendaciones: "", RequiereAutorizacion: false, proximaReservaDescanso: false,
};

const CANTIDADES = [
  ["cantidadSedes", "Cantidad de sedes habilitadas"],
  ["LimitePersonas", "Cantidad máxima de personas"],
  ["LimiteHoras", "Límite de horas de uso"],
  ["reservaPorMes", "Reserva máxima por mes"],
  ["reservaPorSemana", "Reserva máxima por semana"],
  ["reservaPorDia", "Reserva máxima por día"],
  ["proximaReserva", "Proxima reserva autorizada"],
];
const DIAS = [
  ["reservaLunes", "Lunes"], ["reservaMartes", "Martes"], ["reservaMiercoles", "Miércoles"],
  ["reservaJueves", "Jueves"], ["reservaViernes", "Viernes"], ["reservaSabado", "Sábado"], ["reservaDomingo", "Domingo"],
];
const OPCIONES = [
  ["reservaUnica", "Zona única", "(Solo podrá registrar una reserva Para esta zona  y se rechazarán otras reservas si ya fue asignada y aprobada a un usuario en cierto horario)"],
  ["horaCompleta", "Hora completa", "(Se modificará el rango horario de reservas para permitir solo horas completas (por ejemplo, 01:00 PM a 02:00 PM)."],
  ["agregarDetalles", "Agregar detalles", "(Al realizar una reserva, el usuario debe agregar un titulo y una descripción referente a la actividad que realizara en la reserva)"],
  ["bloquearHora", "Reservar todo el día", "(Al activar esta opción, se obligara a los usuarios a generar reservas según el maximo de horas permitidas."],
  ["DepositoBool", "¿Requiere Aporte?", "Solicitara un comprobante de pago al momento de realizar la reserva."],
  ["contarPendientes", "¿Contar reservas pendientes?", "Incluir en el listado de reservas aprobadas a las reservas que aun esten en lista de espera por parte de la administración."],
  ["RequiereAutorizacion", "¿Requiere autorización de administración?", "El residente podra generar reservas pero estas solo seran validas una vez el administrador las autorice."],
  ["proximaReservaDescanso", "¿Esperar 1 hora para la siguiente reserva?", "El residente podra generar reservas 1 hora despues de la ultima reserva aprobada. (Recomendada para Turcos y Saunas)"],
  ["adjuntarPdf", "¿Desea adjuntar archivo PDF?", "Se mostrara un archivo adjunto al momento de realizar la reserva, este puede ser un manual o reglamento de la zona social."],
];

const minutos = (h) => {
  const p = String(h ?? "").split(":");
  if (p.length !== 2) return null;
  const a = parseInt(p[0], 10), b = parseInt(p[1], 10);
  return Number.isNaN(a) || Number.isNaN(b) ? null : a * 60 + b;
};

const s = {
  page: { minHeight: "100vh", background: "#f8f9fb", fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif", paddingBottom: 100 },
  header: { maxWidth: 1100, margin: "0 auto", padding: "28px 16px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  grid: { maxWidth: 1100, margin: "0 auto", padding: "28px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 18 },
  card: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "24px 16px 18px", border: "1.5px solid #e5e7eb", borderRadius: 16, background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" },
  boton: { padding: "10px 16px", borderRadius: 10, border: "1px solid #fca5a5", background: "#fff5f5", color: R, fontWeight: 700, fontSize: 15, cursor: "pointer" },
  botonLleno: { padding: "12px 18px", borderRadius: 10, border: "none", background: R, color: "#fff", fontWeight: 700, fontSize: 16, cursor: "pointer" },
  seccion: { background: "#fff", border: "1px solid #e3e8ef", borderRadius: 12, padding: 18, marginBottom: 16 },
  titulo: { fontSize: 18, fontWeight: 700, margin: "0 0 12px" },
  input: { width: "100%", padding: "12px", fontSize: 16, borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" },
  label: { display: "block", fontSize: 16, fontWeight: 600, margin: "12px 0 6px" },
};

// ───────────────────────── Editor de reglas (editar_mis_zonas_sociales.dart) ─────────────────────────
const EditorZona = ({ codigo, catalogo, onCerrar }) => {
  const [zona, setZona] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      const actual = await cargarZona(codigo, catalogo.id);
      if (actual && typeof actual === "object") {
        setZona({ ...actual, adjuntarPdf: actual.adjuntarPdf ?? false });
      } else {
        await crearZona(codigo, catalogo.id, ZONA_NUEVA_EDITOR);
        Swal.fire({ text: "Se creó la zona social correctamente", timer: 2000, showConfirmButton: false });
        setZona({ ...ZONA_NUEVA_EDITOR });
      }
    })().catch(console.error);
  }, [codigo, catalogo.id]);

  if (!zona) return <AnimacionCarga />;
  const cambiar = (k, v) => setZona(z => ({ ...z, [k]: v }));
  // La app muestra desmarcado lo que no está configurado y solo cambia el valor si se toca.
  const marcado = (k) => zona[k] ?? false;

  const guardar = async () => {
    const mi = minutos(zona.HoraInicio), mf = minutos(zona.HoraFinal);
    if (mi === null || mf === null || mf <= mi) { Swal.fire({ text: "La hora final debe ser posterior a la hora inicial.", confirmButtonColor: R }); return; }
    if (zona.adjuntarPdf && !pdf && !zona.urlPdf) { Swal.fire({ text: "Seleccione un archivo PDF o desactive la opción de adjuntar.", confirmButtonColor: R }); return; }
    setGuardando(true);
    try {
      const datos = { ...zona, usuario: localStorage.getItem("Direccion") || "", fecha: fechaHoraDart() };
      if (zona.adjuntarPdf && pdf) datos.urlPdf = await subirArchivoFirebase(`zonas/${codigo}/${catalogo.id}.pdf`, pdf);
      await crearZona(codigo, catalogo.id, soloCamposZona(datos));
      await Swal.fire({ text: "Cambios guardados", timer: 1800, showConfirmButton: false });
      onCerrar();
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", text: "No se pudieron guardar los cambios.", confirmButtonColor: R });
      setGuardando(false);
    }
  };

  const selectorHora = (k) => (
    <select style={s.input} value={zona[k] ?? ""} onChange={e => cambiar(k, e.target.value)}>
      {!HORAS_INTERMEDIAS.includes(zona[k]) && <option value={zona[k] ?? ""}>{zona[k] ?? ""}</option>}
      {HORAS_INTERMEDIAS.map(h => <option key={h} value={h}>{h}</option>)}
    </select>
  );
  const check = (k) => (
    <input type="checkbox" checked={marcado(k)} onChange={e => cambiar(k, e.target.checked)} style={{ width: 28, height: 28, cursor: "pointer", accentColor: R }} />
  );

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "20px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button style={s.boton} onClick={onCerrar}>← Volver</button>
        <h2 style={{ margin: 0, fontSize: 22 }}>{catalogo.titulo}</h2>
      </div>

      <div style={s.seccion}>
        <p style={s.titulo}>Seleccione el horario de uso de la zona</p>
        <label style={s.label}>Hora inicial</label>{selectorHora("HoraInicio")}
        <label style={s.label}>Hora final</label>{selectorHora("HoraFinal")}
        {CANTIDADES.map(([k, etiqueta]) => (
          <div key={k}>
            <label style={s.label}>{etiqueta}</label>
            <input type="number" min={0} max={300} style={s.input} value={zona[k] ?? 0}
              onChange={e => cambiar(k, Math.max(0, Math.min(300, parseInt(e.target.value, 10) || 0)))} />
          </div>
        ))}
        <p style={{ ...s.titulo, fontSize: 20, marginTop: 20 }}>Dias autorizados:</p>
        {DIAS.map(([k, etiqueta]) => (
          <label key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px", fontSize: 18 }}>
            {etiqueta}{check(k)}
          </label>
        ))}
      </div>

      <div style={s.seccion}>
        <p style={s.titulo}>Opciones adicionales</p>
        {OPCIONES.map(([k, titulo, descripcion]) => (
          <div key={k} style={{ display: "flex", gap: 16, alignItems: "center", padding: "12px 0", borderBottom: "1px solid #eee" }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>{titulo}</p>
              <p style={{ margin: "6px 0 0", fontSize: 15, color: "#444" }}>{descripcion}</p>
            </div>
            {check(k)}
          </div>
        ))}
        {zona.adjuntarPdf && (
          <div style={{ padding: "12px 0" }}>
            <label style={s.label}>Adjunte documento PDF</label>
            <input type="file" accept="application/pdf" onChange={e => setPdf(e.target.files[0] || null)} style={{ fontSize: 16 }} />
            {!pdf && zona.urlPdf && <p style={{ margin: "8px 0 0" }}><a href={zona.urlPdf} target="_blank" rel="noreferrer">Ver PDF actual</a></p>}
          </div>
        )}

        {zona.DepositoBool && (
          <div style={{ border: "1px solid #ccc", borderRadius: 10, padding: 12, margin: "20px 0" }}>
            <p style={{ fontWeight: 700, fontSize: 16, margin: "0 0 12px" }}>Cobro de la zona</p>
            <label style={s.label}>Valor (COP):</label>
            <input type="number" min={0} style={s.input} value={zona.DepositoValor ?? 0} onChange={e => cambiar("DepositoValor", parseInt(e.target.value, 10) || 0)} />
            <label style={s.label}>Texto que verá el residente:</label>
            <input style={s.input} maxLength={80} placeholder={TEXTO_DEPOSITO_POR_DEFECTO}
              defaultValue={(zona.DepositoTexto ?? "").trim() === "" ? TEXTO_DEPOSITO_POR_DEFECTO : zona.DepositoTexto}
              onChange={e => cambiar("DepositoTexto", e.target.value)} />
            <p style={{ color: "#555", margin: "6px 0 0" }}>Ejemplo: Esta zona tiene un costo de alquiler, Cobro por uso, etc.</p>
          </div>
        )}

        <p style={{ ...s.titulo, marginTop: 16 }}>Observaciones y recomendaciones</p>
        <textarea style={{ ...s.input, minHeight: 100 }} value={zona.ObservacionesYRecomendaciones ?? ""} onChange={e => cambiar("ObservacionesYRecomendaciones", e.target.value)} />
      </div>

      <label style={s.label}>Último editor:</label>
      <input style={{ ...s.input, background: "#f3f4f6" }} value={zona.usuario ?? ""} readOnly />
      <label style={s.label}>Última actualización:</label>
      <input style={{ ...s.input, background: "#f3f4f6" }} value={zona.fecha ?? ""} readOnly />

      <button style={{ ...s.botonLleno, width: "100%", marginTop: 20, padding: 16, fontSize: 18 }} disabled={guardando} onClick={guardar}>
        {guardando ? "Guardando..." : "💾 Guardar cambios"}
      </button>
    </div>
  );
};

// ───────────────────────── Listado (gestionar_zonas_sociales.dart) ─────────────────────────
const ListadoZonas = memo(() => {
  const navigate = useNavigate();
  const codigo = localStorage.getItem("CodigoCliente") || "";
  const permisos = localStorage.getItem("Permisos") || "";
  const [conjunto, setConjunto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try { setConjunto(await cargarConjunto(codigo)); }
    catch (e) { console.error(e); }
    setCargando(false);
  }, [codigo]);
  useEffect(() => { cargar(); }, [cargar]);

  // seleccionar_zonas.dart → activa la zona y le escribe las reglas por defecto.
  const agregarZona = async () => {
    const disponibles = ZONAS_CATALOGO.filter(z => (conjunto?.[z.campo] ?? false) !== true);
    if (!disponibles.length) { Swal.fire({ text: "Todas las zonas ya están habilitadas.", confirmButtonColor: R }); return; }
    const { value: campo } = await Swal.fire({
      title: "Seleccionar zona para habilitar",
      input: "select",
      inputOptions: Object.fromEntries(disponibles.map(z => [z.campo, `${z.icono} ${z.titulo}`])),
      inputPlaceholder: "Elija una zona",
      showCancelButton: true, confirmButtonText: "Habilitar", cancelButtonText: "Cancelar", confirmButtonColor: R,
    });
    if (!campo) return;
    const z = ZONAS_CATALOGO.find(x => x.campo === campo);
    try {
      await Promise.all([
        actualizarRegistro(`coordi_ph_clientes/${codigo}/${conjunto.idConjunto}/${z.campo}`, true),
        crearZona(codigo, z.id, ZONA_POR_DEFECTO),
      ]);
      Swal.fire({ text: "Zona agregada correctamente", timer: 1800, showConfirmButton: false });
    } catch {
      Swal.fire({ text: "Se presentó un error en la red", confirmButtonColor: R });
    }
    cargar();
  };

  // eliminar_zonas_sociales_alerta.dart → solo apaga el interruptor; reglas y reservas se conservan.
  const eliminarZona = async (z) => {
    const { isConfirmed } = await Swal.fire({ title: "¿Eliminar zona?", text: z.titulo, icon: "warning", showCancelButton: true, confirmButtonText: "Eliminar", cancelButtonText: "Cancelar", confirmButtonColor: R });
    if (!isConfirmed) return;
    try { await actualizarRegistro(`coordi_ph_clientes/${codigo}/${conjunto.idConjunto}/${z.campo}`, false); }
    catch { Swal.fire({ text: "Error al eliminar la zona", confirmButtonColor: R }); }
    cargar();
  };

  if (cargando) return <AnimacionCarga />;
  if (editando) return <div style={s.page}><BarraSuperior /><EditorZona codigo={codigo} catalogo={editando} onCerrar={() => { setEditando(null); cargar(); }} /><BotonSOS /></div>;

  const activas = zonasActivasDelConjunto(conjunto);
  const esAdmin = permisos === "ADMINISTRADOR" || permisos === "SUPER";
  return (
    <div style={s.page}>
      <BarraSuperior />
      <div style={s.header}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 24 }}>Configura tus zonas sociales</h2>
          <p style={{ margin: 0, fontSize: 16, color: "#6b7280" }}>Zonas sociales activas</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {esAdmin && <button style={s.boton} onClick={() => navigate("/reservas")}>Autorizaciones</button>}
          {esAdmin && <button style={s.boton} onClick={() => navigate("/estadistica")}>Estadísticas</button>}
          <button style={s.boton} onClick={cargar} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
          <button style={s.botonLleno} onClick={agregarZona}>+ Agregar zona</button>
        </div>
      </div>
      {!activas.length
        ? <p style={{ textAlign: "center", padding: 60, color: "#9ca3af", fontSize: 16 }}>No hay zonas activas. Use "+ Agregar zona".</p>
        : <div style={s.grid}>
          {activas.map(z => (
            <div key={z.id} style={s.card}>
              <span style={{ fontSize: 34 }}>{z.icono}</span>
              <span style={{ fontSize: 16, fontWeight: 700, textAlign: "center" }}>{z.titulo}</span>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button style={s.boton} onClick={() => setEditando(z)}>Editar</button>
                <button style={{ ...s.boton, color: "#6b7280", borderColor: "#d1d5db", background: "#fff" }} onClick={() => eliminarZona(z)} title="Eliminar zona"><i className="bi bi-trash" /></button>
              </div>
            </div>
          ))}
        </div>}
      <BotonSOS />
    </div>
  );
});

export default ListadoZonas;
